import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Input } from '../components/ui/input';
import { Activity, Search, User, Clock, ArrowRight, MessageSquare, ArrowRightLeft } from 'lucide-react';
import api from '../utils/api';
import { toast } from 'sonner';

const ACTION_CONFIG = {
  status_change: { label: 'Status Change', color: 'bg-blue-100 text-blue-700', icon: ArrowRight },
  task_created: { label: 'Task Created', color: 'bg-green-100 text-green-700', icon: Activity },
  task_reassigned: { label: 'Task Transferred', color: 'bg-purple-100 text-purple-700', icon: ArrowRightLeft },
  comment_added: { label: 'Comment', color: 'bg-yellow-100 text-yellow-700', icon: MessageSquare },
};

export default function ActivityLogPage() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterAction, setFilterAction] = useState('all');
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');

  useEffect(() => {
    fetchLogs();
  }, []);

  const fetchLogs = async () => {
    try {
      const res = await api.get('/activity-logs?limit=200');
      setLogs(res.data);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Access denied - Admin/Super Admin only');
      } else {
        toast.error('Failed to load activity logs');
      }
    } finally {
      setLoading(false);
    }
  };

  const filteredLogs = logs.filter(log => {
    const matchesSearch = searchTerm === '' ||
      log.task_title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.user_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.comment?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesAction = filterAction === 'all' || log.action === filterAction;
    return matchesSearch && matchesAction;
  });

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHrs = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHrs < 24) return `${diffHrs}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const getActionConfig = (action) => ACTION_CONFIG[action] || { label: action, color: 'bg-slate-100 text-slate-600', icon: Activity };

  if (loading) {
    return <div className="p-8">Loading...</div>;
  }

  return (
    <div className="p-8 min-h-full" style={{
        background: 'linear-gradient(135deg, rgba(91, 111, 214, 0.03) 0%, rgba(16, 185, 129, 0.03) 100%)'
      }}>
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold tracking-tight mb-2" style={{
            background: 'linear-gradient(135deg, #1e1b4b 0%, #0f172a 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent'
          }}>Activity Log</h1>
          <p className="text-lg text-slate-600">Track all task updates and changes across your team</p>
        </div>

        {/* Filters */}
        <div className="flex gap-4 mb-6">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-slate-400" />
            <Input
              placeholder="Search by task, user, or comment..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-white/80"
              data-testid="activity-search-input"
            />
          </div>
          <Select value={filterAction} onValueChange={setFilterAction}>
            <SelectTrigger className="w-48 bg-white/80" data-testid="activity-filter-select">
              <SelectValue placeholder="Filter by action" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Actions</SelectItem>
              <SelectItem value="status_change">Status Changes</SelectItem>
              <SelectItem value="task_created">Task Created</SelectItem>
              <SelectItem value="task_reassigned">Task Transferred</SelectItem>
              <SelectItem value="comment_added">Comments</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Activity Log List */}
        <Card className="border-slate-200 shadow-sm bg-white/80 backdrop-blur-sm" data-testid="activity-log-list">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-semibold flex items-center gap-2">
              <Activity className="h-5 w-5 text-[#5B6FD6]" />
              Recent Activity
              <Badge variant="outline" className="ml-2">{filteredLogs.length}</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {filteredLogs.length === 0 ? (
              <div className="text-center py-12">
                <Activity className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">No activity logs found</p>
              </div>
            ) : (
              <div className="space-y-1">
                {filteredLogs.map((log, index) => {
                  const config = getActionConfig(log.action);
                  const Icon = config.icon;
                  return (
                    <div
                      key={log.id || index}
                      className="flex items-start gap-4 py-3 px-4 rounded-lg hover:bg-slate-50 transition-colors border-b border-slate-50 last:border-0"
                      data-testid={`activity-log-${log.id || index}`}
                    >
                      <div className={`flex-shrink-0 mt-0.5 h-8 w-8 rounded-full flex items-center justify-center ${config.color}`}>
                        <Icon className="h-4 w-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium text-sm text-slate-900">{log.user_name}</span>
                          <Badge className={`text-xs ${config.color}`}>{config.label}</Badge>
                          <span className="text-sm text-slate-600">on</span>
                          <span className="font-medium text-sm text-[#5B6FD6]">{log.task_title}</span>
                        </div>
                        {(log.previous_status || log.new_status) && (
                          <div className="flex items-center gap-2 mt-1 text-xs text-slate-500">
                            {log.previous_status && (
                              <Badge variant="outline" className="text-xs">{log.previous_status}</Badge>
                            )}
                            {log.previous_status && log.new_status && (
                              <ArrowRight className="h-3 w-3" />
                            )}
                            {log.new_status && (
                              <Badge variant="outline" className="text-xs border-[#10B981] text-[#10B981]">{log.new_status}</Badge>
                            )}
                          </div>
                        )}
                        {log.comment && (
                          <p className="text-xs text-slate-500 mt-1 italic">"{log.comment}"</p>
                        )}
                        {log.details && (
                          <p className="text-xs text-slate-400 mt-1">{log.details}</p>
                        )}
                      </div>
                      <div className="flex-shrink-0 text-xs text-slate-400 flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {formatDate(log.created_at)}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
    </div>
  );
}
